import { Button, DynamicTextEditor, Switch, ThresholdPill } from '@aon/aon-tws-common-ui';
import { EditableColumnType } from '@aon/aon-tws-common-ui/build/lib/components/EditableTable';
import React, { useEffect, useState } from 'react';
import HtmlParser from 'html-react-parser';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Constants from '../../../constants';
import { TranslationEditorContainer } from '../../../containers/translations/TranslationEditorContainer';
import ContentConstants from '../../../contentConstants';
import { ISurveyEditModel, ITranslationEditItem } from '../../../models';
import { PrivacyPolicyEditFieldType } from '../../../models/ISurveyPrivacyPolicyEditModel';
import { IApplicationState } from '../../../store';
import { ActionTypes } from '../../../store/ActionTypes';
import { clearPrivacyPolicy, copyPrivacyPolicy } from '../../../store/SurveyPrivacyPolicySlice';
import { ActionCreators, InitDefaultTranslationEditItem } from '../../../store/SurveyStore';
import { BodyCopyMediumContainer, ClearDiv, Col3Col9, LabelAbove, LimitContainer, WarningModal } from '../../common';
import { useSurveyDefaultCultureCode } from '../../../utilities/hooks';
import { setOpenedModalPopupName } from '../../../store/SurveyContentItemsSlice';

const ToggleButtonContainer = styled.div`
	display: flex;
	justify-content: space-between;
`;

export interface IPrivacyPolicyEditBoxProps {
	surveyEditModel: ISurveyEditModel;
	label: string;
	height?: string;
	placeHolder: string;
	translationProperty: string;
	translationDisplayName: string;
	editorInputType: EditableColumnType;
	defaultTranslation: ITranslationEditItem;
	editorFieldType: PrivacyPolicyEditFieldType;
	readOnly: boolean;
	saveInModal: boolean;
}

export function SurveyPrivacyPolicyEditBox(props: IPrivacyPolicyEditBoxProps) {
	const {
		defaultTranslation,
		editorInputType,
		label,
		surveyEditModel,
		height,
		placeHolder,
		translationProperty,
		translationDisplayName,
		editorFieldType,
		readOnly,
		saveInModal,
	} = props;
	const dispatch = useDispatch();
	const defaultCultureCode = useSurveyDefaultCultureCode();
	function HandlePrivacyPolicyChange(value: string) {
		const actionType = GetAction(editorFieldType);
		dispatch({ type: actionType, payload: value });
	}
	function HandlePrivacyPolicyTranslationUpdate() {
		dispatch({ type: ActionTypes.SURVEY_PRIVACYPOLICY_TRANSLATION_UPDATED });
		return;
	}
	function GetAction(fieldType: PrivacyPolicyEditFieldType) {
		switch (fieldType) {
			case PrivacyPolicyEditFieldType.PRIVACYPOLICY_TITLE: {
				return ActionTypes.SURVEY_PRIVACYPOLICY_TITLE_CHANGE;
			}
			case PrivacyPolicyEditFieldType.PRIVACYPOLICY_CONTENT: {
				return ActionTypes.SURVEY_PRIVACYPOLICY_CONTENT_CHANGE;
			}
			case PrivacyPolicyEditFieldType.PRIVACYPOLICY_CHECKBOX: {
				return ActionTypes.SURVEY_PRIVACYPOLICY_CHECKBOX_CHANGE;
			}
			default:
				return;
		}
	}

	return (
		<>
			<ClearDiv clearHeight={'15px'} />
			<Col3Col9 label={label}>
				{editorFieldType === PrivacyPolicyEditFieldType.PRIVACYPOLICY_CHECKBOX && (
					<LabelAbove type='input'>{ContentConstants.INPUTLABEL_PRIVACY_POLICY_CHECKBOX_HELPTEXT}</LabelAbove>
				)}
				<DynamicTextEditor
					inputType={editorInputType}
					enableHtmlEditor={true}
					placeholder={placeHolder}
					onChange={(text: string) => HandlePrivacyPolicyChange(text)}
					value={defaultTranslation != null ? defaultTranslation.textValue : ''}
					htmlEditorMode={'custom'}
					toolbarConfiguration={Constants.customHtmlEditorOptions}
					height={height}
					readOnly={readOnly}
				/>
				{(editorFieldType === PrivacyPolicyEditFieldType.PRIVACYPOLICY_TITLE ||
					editorFieldType === PrivacyPolicyEditFieldType.PRIVACYPOLICY_CHECKBOX) && (
					<LimitContainer className='tws-character-limit-container'>
						<BodyCopyMediumContainer>{ContentConstants.CUSTOM_QUESTION_RECOMMENDED_TEXT}</BodyCopyMediumContainer>
						<ThresholdPill
							size='sm'
							count={defaultTranslation != null && defaultTranslation.textValue ? defaultTranslation.textValue.length : 0}
							threshold={500}
							showThresholdCount={true}
						/>
					</LimitContainer>
				)}
				<ClearDiv clearHeight={editorFieldType === PrivacyPolicyEditFieldType.PRIVACYPOLICY_CONTENT ? '15px' : '0px'} />
				{surveyEditModel.survey.surveyId !== 0 && (
					<TranslationEditorContainer
						enableHtmlEditor={true}
						property={translationProperty}
						inputType={'input'}
						propertyDisplayName={translationDisplayName}
						supportedCultures={surveyEditModel.supportedCultures}
						uniqueId={surveyEditModel.privacyPolicy.surveyPrivacyPolicyId}
						surveyId={surveyEditModel.survey.surveyId}
						defaultTranslation={defaultTranslation ?? InitDefaultTranslationEditItem(defaultCultureCode)}
						notifyUpdate={HandlePrivacyPolicyTranslationUpdate}
						disabled={readOnly}
						saveInModal={saveInModal}
					/>
				)}
			</Col3Col9>
		</>
	);
}

export interface ISurveyPrivacyPolicyProps {
	readOnly: boolean;
	saveInModal: boolean;
}

export function SurveyPrivacyPolicy(props: ISurveyPrivacyPolicyProps) {
	const [privacyPolicyOffWarningModalOpen, setPrivacyPolicyOffWarningModalOpen] = useState(false);
	const [privacyPolicyAddWarningModalOpen, setPrivacyPolicyAddWarningModalOpen] = useState(false);
	const [privacyPolicyClearWarningModalOpen, setPrivacyPolicyClearWarningModalOpen] = useState(false);
	const { isClearOrCopyInProgress } = useSelector((storeState: IApplicationState) => storeState.surveyPrivacyPolicy);
	const { surveyEditModel } = useSelector((storeState: IApplicationState) => storeState.survey);
	const dispatch = useDispatch();
	const { readOnly, saveInModal } = props;
	const { privacyPolicy } = surveyEditModel;
	const showEditBox = privacyPolicy.isActive;

	useEffect(() => {
		if (privacyPolicyClearWarningModalOpen || privacyPolicyAddWarningModalOpen || privacyPolicyClearWarningModalOpen) {
			dispatch(setOpenedModalPopupName(Constants.PRIVACYPOLICY));
		}
	}, [privacyPolicyOffWarningModalOpen, privacyPolicyAddWarningModalOpen, privacyPolicyClearWarningModalOpen, dispatch]);

	function TogglePrivacyPolicy(checked: boolean) {
		if (showEditBox && (privacyPolicy.title?.textValue || privacyPolicy.content?.textValue || privacyPolicy.checkboxText?.textValue)) {
			setPrivacyPolicyOffWarningModalOpen(true);
		} else {
			dispatch(ActionCreators.HandleTogglePrivacyPolicySwitch(checked));
		}
	}

	function AddClearPrivacyPolicy() {
		if (privacyPolicy.useClientPrivacyPolicy) {
			setPrivacyPolicyClearWarningModalOpen(true);
		} else {
			setPrivacyPolicyAddWarningModalOpen(true);
		}
	}

	return (
		<>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_PRIVACYPOLICY}>
				<LabelAbove type='input'>{ContentConstants.INPUTLABEL_PRIVACY_POLICY}</LabelAbove>
				<LabelAbove type='input'>{ContentConstants.INPUTLABEL_PRIVACY_POLICY_CHECKBOX_HELPTEXT}</LabelAbove>
				<ToggleButtonContainer>
					<Switch disabled={readOnly} onChange={(event: any) => TogglePrivacyPolicy(event.target.checked)} checked={showEditBox} />
					{showEditBox && (
						<Button color='secondary' disabled={isClearOrCopyInProgress || readOnly} onClick={AddClearPrivacyPolicy}>
							{privacyPolicy.useClientPrivacyPolicy
								? ContentConstants.BUTTONS_CLEAR_PRIVACY_POLICY
								: ContentConstants.BUTTONS_ADD_PRIVACY_POLICY}
						</Button>
					)}
				</ToggleButtonContainer>
			</Col3Col9>
			{showEditBox && (
				<>
					<SurveyPrivacyPolicyEditBox
						height={'30px'}
						surveyEditModel={surveyEditModel}
						label={ContentConstants.SUBSECTIONHEADERS_TITLE}
						placeHolder={ContentConstants.SUBSECTIONHEADERS_PRIVACYPOLICYTITLE}
						translationProperty={'PrivacyPolicy/Title'}
						translationDisplayName={ContentConstants.SUBSECTIONHEADERS_PRIVACYPOLICYTITLE}
						defaultTranslation={privacyPolicy.title}
						editorInputType={'input'}
						editorFieldType={PrivacyPolicyEditFieldType.PRIVACYPOLICY_TITLE}
						readOnly={readOnly}
						saveInModal={saveInModal}
					/>
					<SurveyPrivacyPolicyEditBox
						surveyEditModel={surveyEditModel}
						label={ContentConstants.SUBSECTIONHEADERS_CONTENT}
						placeHolder={ContentConstants.SECTIONHEADERS_SURVEY_PRIVACYPOLICY_CONTENT_PLACEHOLDER}
						translationProperty={'PrivacyPolicy/Content'}
						translationDisplayName={ContentConstants.SUBSECTIONHEADERS_PRIVACYPOLICYCONTENT}
						defaultTranslation={privacyPolicy.content}
						editorInputType={'textarea'}
						editorFieldType={PrivacyPolicyEditFieldType.PRIVACYPOLICY_CONTENT}
						readOnly={readOnly}
						saveInModal={saveInModal}
					/>
					<SurveyPrivacyPolicyEditBox
						height={'30px'}
						surveyEditModel={surveyEditModel}
						label={ContentConstants.SUBSECTIONHEADERS_CHECKBOXTEXT}
						placeHolder={ContentConstants.SUBSECTIONHEADERS_CHECKBOXTEXT}
						translationProperty={'PrivacyPolicy/CheckBoxText'}
						translationDisplayName={ContentConstants.SUBSECTIONHEADERS_PRIVACYPOLICYCHECKBOXTEXT}
						defaultTranslation={privacyPolicy.checkboxText}
						editorInputType={'input'}
						editorFieldType={PrivacyPolicyEditFieldType.PRIVACYPOLICY_CHECKBOX}
						readOnly={readOnly}
						saveInModal={saveInModal}
					/>
				</>
			)}
			{privacyPolicyOffWarningModalOpen && (
				<WarningModal
					isOpen={privacyPolicyOffWarningModalOpen}
					contentHeadline={ContentConstants.WARNING_PRIVACY_POLICY_OFF_HEADLINE}
					contentBody={<>{HtmlParser(ContentConstants.WARNING_PRIVACY_POLICY_OFF_BODY)}</>}
					onSubmit={() => {
						setPrivacyPolicyOffWarningModalOpen(false);
						dispatch(ActionCreators.HandleTogglePrivacyPolicySwitch(false));
					}}
					onClose={() => setPrivacyPolicyOffWarningModalOpen(false)}
				/>
			)}
			{privacyPolicyAddWarningModalOpen && (
				<WarningModal
					isOpen={privacyPolicyAddWarningModalOpen}
					contentHeadline={ContentConstants.WARNING_ADD_PRIVACY_POLICY_HEADLINE}
					contentBody={<>{HtmlParser(ContentConstants.WARNING_ADD_PRIVACY_POLICY_BODY)}</>}
					onSubmit={() => {
						setPrivacyPolicyAddWarningModalOpen(false);
						dispatch(copyPrivacyPolicy(surveyEditModel.survey.surveyId));
					}}
					onClose={() => setPrivacyPolicyAddWarningModalOpen(false)}
				/>
			)}
			{privacyPolicyClearWarningModalOpen && (
				<WarningModal
					isOpen={privacyPolicyClearWarningModalOpen}
					contentHeadline={ContentConstants.WARNING_CLEAR_PRIVACY_POLICY_HEADLINE}
					contentBody={<>{HtmlParser(ContentConstants.WARNING_CLEAR_PRIVACY_POLICY_BODY)}</>}
					onSubmit={() => {
						setPrivacyPolicyClearWarningModalOpen(false);
						dispatch(clearPrivacyPolicy(surveyEditModel.survey.surveyId));
					}}
					onClose={() => setPrivacyPolicyClearWarningModalOpen(false)}
				/>
			)}
		</>
	);
}
